import isFunction from "lodash/isFunction";
import React from "react";

import type { PolymorphicBoxProps } from "~/components/polymorphic-box";
import { cx } from "~/tools/class-variance";

import styles from "./_skeleton.module.css";
import { SkeletonBoundary } from "./boundary";

const DEFAULT_ELEMENT = "div";

export interface SkeletonBoxProps<TValue> {
	/**
	 * @default null
	 */
	when?: undefined | null | TValue;

	children?: React.ReactNode | ((value: TValue) => React.ReactNode);
}

export const SkeletonBox: <TValue = unknown, TElement extends React.ElementType = typeof DEFAULT_ELEMENT>(
	props: PolymorphicBoxProps<TElement, SkeletonBoxProps<TValue>>,
) => React.ReactNode = React.forwardRef(
	(
		{
			el = DEFAULT_ELEMENT,
			when = null,
			children,
			...props
		}: PolymorphicBoxProps<React.ElementType, SkeletonBoxProps<unknown>>,
		ref,
	) => {
		const skeletonProps =
			when == null
				? {
						className: cx([props.className, styles.skeletonBox]),
						tabIndex: -1,
					}
				: {};

		return (
			<SkeletonBoundary el={el} ref={ref} when={when} {...props} {...skeletonProps}>
				{isFunction(children) ? (when == null ? null : children(when)) : children}
			</SkeletonBoundary>
		);
	},
);
